import { useRef } from "react";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";

const RED = "#d91616";
const RED_SOFT = "#ff3b3b";

// Regular polygon path centered at (cx, cy).
function polyPath(cx, cy, r, sides, rot = 0) {
  const pts = Array.from({ length: sides }).map((_, i) => {
    const a = (2 * Math.PI / sides) * i - Math.PI / 2 + rot;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  });
  return "M" + pts.map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join(" L ") + " Z";
}

// Peptide backbone: repeating N–Cα–C(=O) zig-zag.
const STEP = 88;
const X0 = 44;
const V = Array.from({ length: 13 }).map((_, i) => ({
  x: X0 + i * STEP,
  y: i % 2 === 0 ? 150 : 90,
  role: ["N", "CA", "C"][i % 3], // repeating amide unit
}));
const backbonePath = "M" + V.map((v) => `${v.x} ${v.y}`).join(" L ");

// Carbonyl double bonds + oxygen on every C vertex.
const carbonyls = V.filter((v) => v.role === "C").map((v) => {
  const down = v.y > 120;
  const dir = down ? 1 : -1;
  return {
    l1: `M${v.x - 4} ${v.y + dir * 6} L${v.x - 4} ${v.y + dir * 34}`,
    l2: `M${v.x + 4} ${v.y + dir * 6} L${v.x + 4} ${v.y + dir * 34}`,
    ox: v.x,
    oy: v.y + dir * 48,
  };
});

// Nitrogen atom labels on backbone amide N vertices.
const nitrogens = V.filter((v) => v.role === "N");

// Side chains on Cα vertices — real functional groups.
const alphas = V.filter((v) => v.role === "CA");
// Bonds from each Cα out to its side-chain group.
const sideBonds = [
  `M${alphas[0].x} ${alphas[0].y} L${alphas[0].x} ${alphas[0].y - 30}`, // to imidazole (His)
  `M${alphas[1].x} ${alphas[1].y} L${alphas[1].x} ${alphas[1].y + 30}`, // to phenyl (Phe)
  `M${alphas[2].x} ${alphas[2].y} L${alphas[2].x - 26} ${alphas[2].y - 22}`, // short alkyl
  `M${alphas[3].x} ${alphas[3].y} L${alphas[3].x + 26} ${alphas[3].y + 22}`, // short alkyl
];
// Imidazole (His) — 5-membered ring above alpha[0].
const imidazole = polyPath(alphas[0].x, alphas[0].y - 52, 22, 5, 0);
// Phenyl (Phe) — 6-membered ring below alpha[1].
const phenyl = polyPath(alphas[1].x, alphas[1].y + 54, 24, 6, Math.PI / 6);
const phenylInner = alphas[1].x; // for aromatic inner circle center

export default function MoleculeDraw() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 0.85", "end 0.5"],
  });
  const draw = useSpring(scrollYProgress, { stiffness: 80, damping: 24, mass: 0.4 });
  const labelOpacity = useTransform(scrollYProgress, [0.55, 0.9], [0, 1]);
  const nodeScale = useTransform(scrollYProgress, [0.5, 0.92], [0, 1]);
  const glow = useTransform(scrollYProgress, [0, 1], [0.2, 1]);

  return (
    <div ref={ref} data-testid="molecule-draw" className="relative border-t border-[#141414] py-20 md:py-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <motion.svg
          viewBox="0 0 1180 260"
          className="w-full overflow-visible"
          role="img"
          aria-label="self-drawing peptide bond backbone with histidine and phenylalanine side chains"
          style={{ filter: "drop-shadow(0 0 22px rgba(217,22,22,0.22))", opacity: glow }}
        >
          {/* Backbone */}
          <motion.path
            d={backbonePath}
            fill="none"
            stroke={RED}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ pathLength: draw }}
          />
          {/* Side-chain bonds */}
          {sideBonds.map((d, i) => (
            <motion.path key={`sb-${i}`} d={d} fill="none" stroke={RED} strokeWidth="2" strokeLinecap="round" style={{ pathLength: draw }} />
          ))}
          {/* Carbonyl double bonds */}
          {carbonyls.map((c, i) => (
            <g key={`co-${i}`}>
              <motion.path d={c.l1} fill="none" stroke={RED} strokeWidth="1.8" strokeLinecap="round" style={{ pathLength: draw }} />
              <motion.path d={c.l2} fill="none" stroke={RED} strokeWidth="1.8" strokeLinecap="round" style={{ pathLength: draw }} />
            </g>
          ))}
          {/* Rings */}
          <motion.path d={imidazole} fill="none" stroke={RED_SOFT} strokeWidth="2" strokeLinejoin="round" style={{ pathLength: draw }} />
          <motion.path d={phenyl} fill="none" stroke={RED_SOFT} strokeWidth="2" strokeLinejoin="round" style={{ pathLength: draw }} />
          <motion.circle
            cx={phenylInner}
            cy={alphas[1].y + 54}
            r="13"
            fill="none"
            stroke={RED_SOFT}
            strokeWidth="1.4"
            style={{ pathLength: draw }}
          />

          {/* Alpha-carbon nodes */}
          {alphas.map((v, i) => (
            <motion.circle
              key={`ca-${i}`}
              cx={v.x}
              cy={v.y}
              r="4.5"
              fill={RED}
              style={{ scale: nodeScale, transformOrigin: `${v.x}px ${v.y}px` }}
            />
          ))}

          {/* Nitrogen atom labels */}
          {nitrogens.map((v, i) => (
            <motion.text
              key={`n-${i}`}
              x={v.x}
              y={v.y > 120 ? v.y + 20 : v.y - 12}
              textAnchor="middle"
              className="font-mono2"
              fontSize="12"
              fill="#ffffff"
              style={{ opacity: labelOpacity }}
            >
              N
            </motion.text>
          ))}
          {/* Oxygen atom labels */}
          {carbonyls.map((c, i) => (
            <motion.text
              key={`o-${i}`}
              x={c.ox}
              y={c.oy + (c.oy > 130 ? 12 : 4)}
              textAnchor="middle"
              className="font-mono2"
              fontSize="12"
              fill={RED_SOFT}
              style={{ opacity: labelOpacity }}
            >
              O
            </motion.text>
          ))}
          {/* Side-chain labels */}
          <motion.text x={alphas[0].x + 30} y={alphas[0].y - 60} className="font-mono2" fontSize="10" fill="rgba(255,255,255,0.5)" style={{ opacity: labelOpacity }}>
            His
          </motion.text>
          <motion.text x={alphas[1].x + 32} y={alphas[1].y + 58} className="font-mono2" fontSize="10" fill="rgba(255,255,255,0.5)" style={{ opacity: labelOpacity }}>
            Phe
          </motion.text>
        </motion.svg>
      </div>
    </div>
  );
}
